import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageHeader from "../../core/common/header/PageHeader";
import {
  FaPlus,
  FaSync,
  FaEdit,
  FaTrash,
  FaUsers,
  FaSpinner,
  FaChartBar,
  FaClipboardList,
  FaEye,
  FaClock,
  FaListAlt,
} from "react-icons/fa";
import AdminBatchQuizSummary from "../quiz/AdminBatchQuizSummary";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

type Quiz = {
  id: number;
  title: string;
  chapter?: string;
  level?: string;
};

type Question = {
  id: number;
  questionText: string;
  options: string[] | string;
  correctAnswer: string;
  explanation?: string;
};

type AssignedBatch = {
  id: number | string;
  quizId: number;
  quizTitle: string;
  quizLevel: string;
  batchId: number;
  batchName: string;
  assignedDate: string;
  deadline: string;
  status: string;
};

const defaultQuestion = {
  id: 0,
  questionText: "",
  options: ["", "", "", ""],
  correctAnswer: "",
  explanation: "",
};

const AdminManageQuizzes = () => {
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const navigate = useNavigate();

  // Modal state
  const [viewingQuizId, setViewingQuizId] = useState<number | null>(null);
  const [quizQuestions, setQuizQuestions] = useState<Question[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);

  // Edit/Add state
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [formQuestion, setFormQuestion] = useState<Question>(defaultQuestion);
  const [formError, setFormError] = useState("");
  const [showBatchResultsModal, setShowBatchResultsModal] = useState(false);

  // Assigned Batches state
  const [showAssignedBatchesModal, setShowAssignedBatchesModal] =
    useState(false);
  const [assignedBatches, setAssignedBatches] = useState<AssignedBatch[]>([]);
  const [assignedBatchesLoading, setAssignedBatchesLoading] = useState(false);
  const [activeAssignedTab, setActiveAssignedTab] = useState("A1");

  const fetchQuizzes = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/quizzes/mentor`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch quizzes");
      const data = await res.json();
      setQuizzes(Array.isArray(data) ? data : []);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load quizzes");
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignedBatches = async () => {
    setAssignedBatchesLoading(true);
    try {
      // Fetch all quizzes first
      const quizzesRes = await fetch(`${API_BASE}/api/quizzes/mentor`, {
        credentials: "include",
      });
      if (!quizzesRes.ok) throw new Error("Failed to fetch quizzes");
      const quizzes = await quizzesRes.json();

      // Create a batch lookup map (will be populated as we fetch assignments)
      const batchMap = new Map();

      // Try to fetch batches - if it fails, we'll use batch IDs
      try {
        const batchesRes = await fetch(`${API_BASE}/api/admin/batches`, {
          credentials: "include",
        });
        if (batchesRes.ok) {
          const batches = await batchesRes.json();
          batches.forEach((batch: any) => {
            batchMap.set(batch.id, batch.name);
          });
        }
      } catch (err) {
        console.log("Could not fetch batch names, will use IDs");
      }

      // For each quiz, fetch its assignments
      const allAssignments: AssignedBatch[] = [];

      for (const quiz of quizzes) {
        try {
          const assignRes = await fetch(
            `${API_BASE}/api/quizzes/${quiz.id}/assigned-batches`,
            { credentials: "include" }
          );

          if (assignRes.ok) {
            const assignments = await assignRes.json();

            // Transform the assignments
            assignments.forEach((assignment: any) => {
              const deadline = new Date(assignment.deadline);
              const now = new Date();

              // Determine status
              let status = "active";
              if (deadline < now) {
                status = "completed";
              } else if (
                deadline > new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000)
              ) {
                status = "upcoming";
              }

              allAssignments.push({
                id: `${quiz.id}-${
                  assignment.batchId
                }-${Date.now()}-${Math.random()}`,
                quizId: quiz.id,
                quizTitle: quiz.title,
                quizLevel: quiz.level || "Unspecified",
                batchId: assignment.batchId,
                batchName:
                  batchMap.get(assignment.batchId) ||
                  `Batch ${assignment.batchId}`,
                assignedDate:
                  assignment.assignedDate ||
                  assignment.createdAt ||
                  new Date().toISOString(),
                deadline: assignment.deadline,
                status: status,
              });
            });
          }
        } catch (err) {
          console.error(`Error fetching assignments for quiz ${quiz.id}:`, err);
        }
      }

      console.log("📊 Loaded assignments:", allAssignments);
      setAssignedBatches(allAssignments);
    } catch (err) {
      console.error("Failed to load assigned batches:", err);
      setAssignedBatches([]);
    } finally {
      setAssignedBatchesLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await fetchQuizzes();
    setRefreshing(false);
  };

  useEffect(() => {
    fetchQuizzes();
  }, []);

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this quiz?")) return;
    try {
      const res = await fetch(`${API_BASE}/api/quizzes/mentor/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to delete quiz");
      setQuizzes((prev) => prev.filter((q) => q.id !== id));
    } catch (err) {
      alert("Failed to delete quiz.");
    }
  };

  const handleRemoveAssignment = async (
    quizId: number,
    batchId: number,
    assignmentId: string | number
  ) => {
    if (!window.confirm("Remove this assignment?")) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/quizzes/${quizId}/batches/${batchId}`,
        { method: "DELETE", credentials: "include" }
      );
      if (!res.ok) throw new Error("Failed to remove assignment");
      setAssignedBatches((prev) => prev.filter((a) => a.id !== assignmentId));
    } catch (err) {
      alert("Failed to remove assignment.");
    }
  };

  const handleViewAssignedBatches = () => {
    setShowAssignedBatchesModal(true);
    fetchAssignedBatches();
  };

  // Group quizzes by level
  const quizzesByLevel = quizzes.reduce<Record<string, Quiz[]>>((acc, quiz) => {
    const level = quiz.level || "Unspecified";
    if (!acc[level]) acc[level] = [];
    acc[level].push(quiz);
    return acc;
  }, {});

  // Sort levels in order: A1, A2, B1, B2, C1, C2
  const levelOrder = ["A1", "A2", "B1", "B2", "C1", "C2", "Unspecified"];
  const sortedLevels = Object.keys(quizzesByLevel).sort((a, b) => {
    const indexA = levelOrder.indexOf(a);
    const indexB = levelOrder.indexOf(b);
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  });

  // Group assigned batches by level
  const assignedBatchesByLevel = assignedBatches.reduce<
    Record<string, AssignedBatch[]>
  >((acc, assignment) => {
    const level = assignment.quizLevel || "Unspecified";
    if (!acc[level]) acc[level] = [];
    acc[level].push(assignment);
    return acc;
  }, {});

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<
      string,
      { label: string; icon: JSX.Element; className: string }
    > = {
      active: {
        label: "Active",
        icon: <FaClock />,
        className: "bg-green-100 text-green-800",
      },
      upcoming: {
        label: "Upcoming",
        icon: <FaClock />,
        className: "bg-yellow-100 text-yellow-800",
      },
      completed: {
        label: "Completed",
        icon: <FaClock />,
        className: "bg-blue-100 text-blue-800",
      },
      expired: {
        label: "Expired",
        icon: <FaClock />,
        className: "bg-red-100 text-red-800",
      },
    };

    const config = statusConfig[status.toLowerCase()] || statusConfig.active;

    return (
      <span
        className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-semibold ${config.className}`}
      >
        {config.icon}
        {config.label}
      </span>
    );
  };

  // View questions logic
  const handleViewQuestions = async (quizId: number) => {
    setViewingQuizId(quizId);
    setQuestionsLoading(true);
    setEditingQuestion(null);
    setIsAdding(false);
    setFormError("");
    try {
      const res = await fetch(
        `${API_BASE}/api/quizzes/mentor/${quizId}/questions`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error("Failed to fetch questions");
      const data = await res.json();
      setQuizQuestions(Array.isArray(data) ? data : []);
    } catch (err) {
      setQuizQuestions([]);
    } finally {
      setQuestionsLoading(false);
    }
  };

  const handleCloseQuestions = () => {
    setViewingQuizId(null);
    setQuizQuestions([]);
    setEditingQuestion(null);
    setIsAdding(false);
    setFormError("");
  };

  const handleEditQuestion = (q: Question) => {
    setEditingQuestion(q);
    setIsAdding(false);
    setFormQuestion({
      ...q,
      options: Array.isArray(q.options)
        ? [...q.options]
        : JSON.parse(q.options || "[]"),
    });
    setFormError("");
  };

  const handleDeleteQuestion = async (questionId: number) => {
    if (!window.confirm("Delete this question?")) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/quizzes/mentor/questions/${questionId}`,
        { method: "DELETE", credentials: "include" }
      );
      if (!res.ok) throw new Error("Failed to delete question");
      setQuizQuestions((prev) => prev.filter((q) => q.id !== questionId));
    } catch (err) {
      alert("Failed to delete question.");
    }
  };

  const handleAddQuestion = () => {
    setIsAdding(true);
    setEditingQuestion(null);
    setFormQuestion({ ...defaultQuestion });
    setFormError("");
  };

  const handleFormChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
    idx?: number
  ) => {
    const { name, value } = e.target;
    if (name === "options" && typeof idx === "number") {
      setFormQuestion((q) => {
        const newOpts = Array.isArray(q.options) ? [...q.options] : [];
        newOpts[idx] = value;
        return { ...q, options: newOpts };
      });
    } else {
      setFormQuestion((q) => ({ ...q, [name]: value }));
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");
    if (!viewingQuizId) return;
    const payload = {
      questionText: formQuestion.questionText,
      options: Array.isArray(formQuestion.options)
        ? formQuestion.options
        : JSON.parse(formQuestion.options || "[]"),
      correctAnswer: formQuestion.correctAnswer,
      explanation: formQuestion.explanation,
    };
    try {
      let res, data: Question;
      if (editingQuestion) {
        res = await fetch(
          `${API_BASE}/api/quizzes/mentor/questions/${editingQuestion.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(payload),
          }
        );
        if (!res.ok) throw new Error("Failed to update question");
        data = await res.json();
        setQuizQuestions((prev) =>
          prev.map((q) => (q.id === editingQuestion.id ? { ...q, ...data } : q))
        );
        setEditingQuestion(null);
      } else if (isAdding) {
        res = await fetch(
          `${API_BASE}/api/quizzes/mentor/${viewingQuizId}/questions`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(payload),
          }
        );
        if (!res.ok) throw new Error("Failed to add question");
        data = await res.json();
        setQuizQuestions((prev) => [...prev, data]);
        setIsAdding(false);
      }
      setFormQuestion({ ...defaultQuestion });
    } catch (err: any) {
      setFormError(err.message || "Error");
    }
  };

  const handleCancelForm = () => {
    setEditingQuestion(null);
    setIsAdding(false);
    setFormQuestion({ ...defaultQuestion });
    setFormError("");
  };

  const renderQuizTable = (level: string, quizList: Quiz[]) => {
    return (
      <div className="mb-10" key={level}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-bold text-[#009fe3] flex items-center gap-2">
            <FaClipboardList />
            Level: {level}
          </h3>
          <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-md">
            {quizList.length} quiz{quizList.length !== 1 ? "zes" : ""}
          </span>
        </div>
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                    Quiz Title
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                    Chapter
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                    Level
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                    Questions
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {quizList.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="py-10 text-center text-gray-400 font-semibold"
                    >
                      No quizzes found for this level.
                    </td>
                  </tr>
                ) : (
                  quizList.map((quiz, index) => (
                    <tr
                      key={quiz.id}
                      className={`${
                        index % 2 === 1 ? "bg-gray-50" : "bg-white"
                      } hover:bg-blue-50 transition-colors`}
                    >
                      <td className="px-4 py-3">
                        <span className="font-semibold text-gray-900">
                          {quiz.title}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-gray-700">
                          {quiz.chapter || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-blue-100 text-blue-800">
                          {quiz.level}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleViewQuestions(quiz.id)}
                          className="inline-flex items-center gap-1 px-3 py-2 bg-amber-100 text-amber-800 rounded-md hover:bg-amber-200 transition-colors text-sm font-semibold"
                          title="View Questions"
                        >
                          <FaEye size={14} />
                          View
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() =>
                              navigate(
                                `/admin/manage-quizzes/${quiz.id}/assign`
                              )
                            }
                            className="inline-flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-semibold"
                            title="Assign to Batches"
                          >
                            <FaUsers size={14} />
                            Assign
                          </button>
                          <button
                            onClick={() =>
                              navigate(`/admin/manage-quizzes/${quiz.id}/edit`)
                            }
                            className="inline-flex items-center gap-1 px-3 py-2 bg-[#009fe3] text-white rounded-md hover:bg-cyan-600 transition-colors text-sm font-semibold"
                            title="Edit Quiz"
                          >
                            <FaEdit size={14} />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(quiz.id)}
                            className="inline-flex items-center gap-1 px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-semibold"
                            title="Delete Quiz"
                          >
                            <FaTrash size={14} />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderAssignedBatchesTable = (level: string) => {
    const assignments = assignedBatchesByLevel[level] || [];

    if (assignments.length === 0) {
      return (
        <div className="text-center text-gray-400 py-10 bg-white rounded-lg shadow">
          <FaClipboardList className="mx-auto h-12 w-12 text-gray-300 mb-3" />
          <p className="font-semibold">No assignments for this level yet.</p>
        </div>
      );
    }

    // Get unique batches with their latest assignment date
    const batchAssignmentDates = new Map();
    assignments.forEach((a) => {
      const currentDate = batchAssignmentDates.get(a.batchId);
      if (!currentDate || new Date(a.assignedDate) > new Date(currentDate)) {
        batchAssignmentDates.set(a.batchId, a.assignedDate);
      }
    });

    const uniqueBatches = Array.from(
      new Map(
        assignments.map((a) => [
          a.batchId,
          { id: a.batchId, name: a.batchName },
        ])
      ).values()
    ).sort((a, b) => {
      // Sort by latest assignment date descending (newest first)
      const dateA = new Date(batchAssignmentDates.get(a.id) || 0);
      const dateB = new Date(batchAssignmentDates.get(b.id) || 0);
      return dateB.getTime() - dateA.getTime();
    });

    // Create a map for quick lookup: batchId-quizId -> assignment
    const assignmentMap = new Map();
    assignments.forEach((a) => {
      assignmentMap.set(`${a.batchId}-${a.quizId}`, a);
    });

    // Always show Quiz 1 to Quiz 14
    const quizColumns = Array.from({ length: 14 }, (_, i) => i + 1);

    return (
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead className="sticky top-0 z-20">
              <tr className="bg-gradient-to-r from-[#009fe3] to-cyan-500">
                <th className="sticky left-0 z-30 bg-[#009fe3] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-white border-r-2 border-white">
                  Batch Name
                </th>
                {quizColumns.map((quizNum) => (
                  <th
                    key={quizNum}
                    className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider text-white border-r border-cyan-400 min-w-[100px]"
                  >
                    <span className="font-bold text-base">Quiz {quizNum}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {uniqueBatches.map((batch, batchIdx) => (
                <tr
                  key={batch.id}
                  className={`${
                    batchIdx % 2 === 0 ? "bg-white" : "bg-gray-50"
                  } hover:bg-blue-50 transition-colors`}
                >
                  <td className="sticky left-0 z-10 bg-inherit px-4 py-3 font-semibold text-gray-900 border-r-2 border-gray-200">
                    {batch.name}
                  </td>
                  {quizColumns.map((quizNum) => {
                    // Find assignment for this batch and quiz number
                    const assignment = Array.from(assignmentMap.values()).find(
                      (a) =>
                        a.batchId === batch.id &&
                        assignments.findIndex(
                          (asn) =>
                            asn.quizId === a.quizId && asn.batchId === a.batchId
                        ) ===
                          assignments
                            .filter((asn) => asn.batchId === batch.id)
                            .sort((x, y) => x.quizId - y.quizId)
                            .findIndex((asn) => asn.quizId === a.quizId) &&
                        assignments
                          .filter((asn) => asn.batchId === batch.id)
                          .sort((x, y) => x.quizId - y.quizId)
                          .findIndex((asn) => asn.quizId === a.quizId) ===
                          quizNum - 1
                    );

                    // Get the Nth quiz for this batch (sorted by quizId)
                    const batchAssignments = assignments
                      .filter((a) => a.batchId === batch.id)
                      .sort((x, y) => x.quizId - y.quizId);

                    const actualAssignment = batchAssignments[quizNum - 1];

                    return (
                      <td
                        key={`${batch.id}-quiz${quizNum}`}
                        className="px-3 py-3 text-center border-r border-gray-200"
                      >
                        {actualAssignment ? (
                          <div className="flex flex-col items-center gap-1">
                            <div className="text-sm font-semibold text-gray-900">
                              {new Date(
                                actualAssignment.deadline
                              ).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                              })}
                            </div>
                            <div className="text-xs text-gray-500">
                              {new Date(
                                actualAssignment.deadline
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                              })}
                            </div>
                            <div className="mt-1">
                              {getStatusBadge(actualAssignment.status)}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-300 text-xl">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaClipboardList />}
        title="Manage Quizzes"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Manage Quizzes" },
        ]}
      />

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8 bg-white rounded-lg shadow-lg p-4">
          <button
            onClick={() => navigate("/admin/create-quiz")}
            className="flex items-center justify-center gap-2 px-5 py-3 rounded-md font-semibold bg-[#009fe3] hover:bg-cyan-600 text-white shadow-md transition flex-1 sm:flex-initial"
          >
            <FaPlus />
            Create Quiz
          </button>
          <button
            onClick={handleViewAssignedBatches}
            className="flex items-center justify-center gap-2 px-5 py-3 rounded-md font-semibold bg-purple-600 hover:bg-purple-700 text-white shadow-md transition flex-1 sm:flex-initial"
          >
            <FaListAlt />
            View Assigned Batches
          </button>
          <button
            onClick={() => setShowBatchResultsModal(true)}
            className="flex items-center justify-center gap-2 px-5 py-3 rounded-md font-semibold bg-green-600 hover:bg-green-700 text-white shadow-md transition flex-1 sm:flex-initial"
          >
            <FaChartBar />
            Batch Results
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-md">
            <p className="font-semibold">Error</p>
            <p>{error}</p>
          </div>
        )}

        {loading ? (
          <div className="text-center text-[#009fe3] font-semibold py-20 text-xl flex items-center justify-center gap-2">
            <FaSpinner className="animate-spin" />
            Loading quizzes...
          </div>
        ) : (
          <>
            {sortedLevels.length === 0 ? (
              <div className="text-center text-gray-400 py-10 bg-white rounded-lg shadow-lg">
                <FaClipboardList className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Quizzes Yet</h3>
                <p className="mb-4">Create your first quiz to get started!</p>
                <button
                  onClick={() => navigate("/admin/create-quiz")}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#009fe3] text-white rounded-md hover:bg-cyan-600 transition-colors font-semibold"
                >
                  <FaPlus />
                  Create Quiz
                </button>
              </div>
            ) : (
              sortedLevels.map((level) =>
                renderQuizTable(level, quizzesByLevel[level])
              )
            )}
          </>
        )}
      </main>

      {/* Questions Modal */}
      {viewingQuizId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
          onClick={handleCloseQuestions}
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleCloseQuestions}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 text-3xl font-bold"
              aria-label="Close"
            >
              &times;
            </button>
            <h3 className="text-2xl font-bold mb-6 text-[#009fe3] flex items-center gap-2">
              <FaClipboardList />
              Quiz Questions
            </h3>

            {questionsLoading ? (
              <div className="text-center text-[#009fe3] flex items-center justify-center gap-2 py-10">
                <FaSpinner className="animate-spin" />
                Loading questions...
              </div>
            ) : (
              <>
                {/* Edit/Add Form */}
                {(editingQuestion || isAdding) && (
                  <form
                    onSubmit={handleFormSubmit}
                    className="mb-6 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 p-6 rounded-lg shadow"
                  >
                    <h4 className="font-bold text-lg mb-4 text-gray-900">
                      {editingQuestion ? "Edit Question" : "Add New Question"}
                    </h4>
                    <div className="mb-4">
                      <label className="block font-semibold mb-2 text-gray-700">
                        Question Text *
                      </label>
                      <textarea
                        name="questionText"
                        value={formQuestion.questionText}
                        onChange={handleFormChange}
                        required
                        rows={3}
                        className="w-full border border-gray-300 p-3 rounded-md focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                        style={{ fontSize: "16px" }}
                      />
                    </div>
                    {[0, 1, 2, 3].map((idx) => (
                      <div className="mb-3" key={idx}>
                        <label className="block font-medium text-gray-700 mb-1">
                          Option {idx + 1} *
                        </label>
                        <input
                          type="text"
                          name="options"
                          value={formQuestion.options[idx] || ""}
                          onChange={(e) => handleFormChange(e, idx)}
                          required
                          className="w-full border border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                          style={{ fontSize: "16px" }}
                        />
                      </div>
                    ))}
                    <div className="mb-4">
                      <label className="block font-semibold mb-2 text-gray-700">
                        Correct Answer *
                      </label>
                      <select
                        name="correctAnswer"
                        value={formQuestion.correctAnswer}
                        onChange={handleFormChange}
                        required
                        className="w-full border border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                        style={{ fontSize: "16px" }}
                      >
                        <option value="">Select Correct Answer</option>
                        {(Array.isArray(formQuestion.options)
                          ? formQuestion.options
                          : JSON.parse(formQuestion.options || "[]")
                        ).map((opt: string, idx: number) => (
                          <option key={idx} value={opt}>
                            {opt || `Option ${idx + 1}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-4">
                      <label className="block font-semibold mb-2 text-gray-700">
                        Explanation (optional)
                      </label>
                      <textarea
                        name="explanation"
                        value={formQuestion.explanation}
                        onChange={handleFormChange}
                        rows={2}
                        className="w-full border border-gray-300 p-3 rounded-md focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                        style={{ fontSize: "16px" }}
                      />
                    </div>
                    {formError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm mb-4">
                        {formError}
                      </div>
                    )}
                    <div className="flex gap-3">
                      <button
                        type="submit"
                        className="px-6 py-3 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white rounded-md font-semibold hover:shadow-lg transition"
                      >
                        {editingQuestion ? "Save Changes" : "Add Question"}
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelForm}
                        className="px-6 py-3 bg-gray-200 text-gray-700 rounded-md font-semibold hover:bg-gray-300 transition"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                {/* Questions List */}
                {quizQuestions.length > 0 ? (
                  <div className="space-y-4">
                    {quizQuestions.map((q, index) => (
                      <div
                        key={q.id}
                        className="border border-gray-200 p-5 rounded-lg shadow-sm bg-white hover:shadow-md transition"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-start gap-3 mb-3">
                              <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-800 font-bold rounded-md text-sm flex-shrink-0">
                                {index + 1}
                              </span>
                              <div className="font-semibold text-lg text-gray-900">
                                {q.questionText}
                              </div>
                            </div>
                            <ul className="ml-11 space-y-1 text-gray-700">
                              {(Array.isArray(q.options)
                                ? q.options
                                : JSON.parse(q.options || "[]")
                              ).map((opt: string, i: number) => (
                                <li
                                  key={i}
                                  className={`text-sm ${
                                    opt === q.correctAnswer
                                      ? "text-green-700 font-semibold"
                                      : ""
                                  }`}
                                >
                                  {String.fromCharCode(65 + i)}. {opt}
                                  {opt === q.correctAnswer && " ✓"}
                                </li>
                              ))}
                            </ul>
                            {q.explanation && (
                              <div className="ml-11 mt-3 text-sm text-gray-600 italic bg-gray-50 p-3 rounded-md">
                                <strong>Explanation:</strong> {q.explanation}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-2 ml-4">
                            <button
                              className="px-3 py-1 text-sm bg-[#009fe3] text-white rounded-md hover:bg-cyan-600 transition"
                              onClick={() => handleEditQuestion(q)}
                            >
                              Edit
                            </button>
                            <button
                              className="px-3 py-1 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition"
                              onClick={() => handleDeleteQuestion(q.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray-400 py-10">
                    <FaClipboardList className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                    <p>No questions found for this quiz.</p>
                  </div>
                )}

                {/* Add Question Button */}
                {!isAdding && !editingQuestion && (
                  <div className="mt-6">
                    <button
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white rounded-md font-semibold hover:shadow-lg transition"
                      onClick={handleAddQuestion}
                    >
                      <FaPlus />
                      Add New Question
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Assigned Batches Modal */}
      {showAssignedBatchesModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
          onClick={() => setShowAssignedBatchesModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full max-w-[95vw] h-[90vh] flex flex-col relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowAssignedBatchesModal(false)}
              className="absolute top-4 right-4 z-10 text-gray-600 hover:text-gray-900 text-3xl font-bold bg-white rounded-full w-10 h-10 flex items-center justify-center shadow-md hover:shadow-lg transition-all"
              aria-label="Close"
            >
              &times;
            </button>

            {/* Header - Fixed */}
            <div className="flex-shrink-0 p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-[#009fe3] flex items-center gap-2">
                <FaListAlt />
                Assigned Batches & Deadlines
              </h3>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
              {assignedBatchesLoading ? (
                <div className="text-center text-[#009fe3] flex items-center justify-center gap-2 py-10">
                  <FaSpinner className="animate-spin" />
                  Loading assigned batches...
                </div>
              ) : (
                <>
                  {/* Level Tabs - Sticky */}
                  <div className="sticky top-0 bg-white z-10 pb-4 mb-6 border-b border-gray-200">
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {levelOrder.slice(0, 6).map((level) => {
                        const count =
                          assignedBatchesByLevel[level]?.length || 0;
                        return (
                          <button
                            key={level}
                            onClick={() => setActiveAssignedTab(level)}
                            className={`px-4 py-2 rounded-t-lg font-semibold text-sm whitespace-nowrap transition-colors ${
                              activeAssignedTab === level
                                ? "bg-[#009fe3] text-white"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                          >
                            Level {level}
                            {count > 0 && (
                              <span
                                className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                                  activeAssignedTab === level
                                    ? "bg-white/20"
                                    : "bg-gray-300"
                                }`}
                              >
                                {count}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Table for Active Tab */}
                  {renderAssignedBatchesTable(activeAssignedTab)}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Batch Results Modal */}
      {showBatchResultsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
          onClick={() => setShowBatchResultsModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-auto p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowBatchResultsModal(false)}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 text-3xl font-bold"
              aria-label="Close"
            >
              &times;
            </button>
            <h3 className="text-2xl font-bold mb-6 text-[#009fe3] flex items-center gap-2">
              <FaChartBar />
              Batch Quiz Results Summary
            </h3>
            <AdminBatchQuizSummary />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManageQuizzes;
